"use client";

import { useCallback, useEffect, useRef } from "react";

import { trackKundaliFormStep, type KundaliStepAction } from "@/lib/analytics-kundali";
import type { KundaliProgressiveStep } from "./KundaliFormCard.progressive";

const STEP_NAMES = ["identity", "chart", "contact"] as const;

type Context = {
  enabled: boolean;
  step: KundaliProgressiveStep;
  language: string;
  source: string;
  sourcePath?: string;
};

type JourneyState = {
  activeStep: KundaliProgressiveStep | null;
  occurrence: number;
  visits: [number, number, number];
  emitted: Set<string>;
};

export function useKundaliProgressiveTelemetry(context: Context) {
  const state = useRef<JourneyState>({
    activeStep: null,
    occurrence: 0,
    visits: [0, 0, 0],
    emitted: new Set(),
  });
  const emit = useCallback(
    (action: KundaliStepAction, step: KundaliProgressiveStep) => {
      if (!context.enabled) return;
      const occurrence = ensureVisit(state.current, step);
      const key = `${step}:${occurrence}:${action}`;
      if (state.current.emitted.has(key)) return;
      state.current.emitted.add(key);
      trackKundaliFormStep({
        step: STEP_NAMES[step],
        action,
        occurrence,
        language: context.language,
        source: context.source,
        source_path: context.sourcePath ?? window.location.pathname,
      });
    },
    [context.enabled, context.language, context.source, context.sourcePath]
  );

  useEffect(() => emit("view", context.step), [context.step, emit]);
  return emit;
}

function ensureVisit(state: JourneyState, step: KundaliProgressiveStep) {
  if (state.activeStep !== step) {
    state.visits[step] += 1;
    state.activeStep = step;
    state.occurrence = state.visits[step];
  }
  return state.occurrence;
}
